import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, TrendingUp, Zap, Crown, Briefcase, Infinity } from 'lucide-react';

const packages = [
  {
    id: 1,
    name: 'Mini',
    subtitle: 'Düzen Kur',
    price: '3.900',
    period: 'tek sefer',
    icon: Sparkles,
    popular: false,
    features: [
      '4 Post tasarım',
      '8 Story şablonu',
      'Profil düzeni (bio + highlight kapak)',
      '1 revize turu'
    ],
    color: '#d4af37'
  },
  {
    id: 2,
    name: 'Başlangıç',
    subtitle: 'Aylık Set',
    price: '6.900',
    period: 'aylık',
    icon: TrendingUp,
    popular: false,
    features: [
      '8 Post tasarım',
      '12 Story tasarım',
      '2 Reels kapak / thumbnail',
      '1 revize turu'
    ],
    color: '#d4af37'
  },
  {
    id: 3,
    name: 'Büyüme',
    subtitle: 'Daha Görünür',
    price: '10.900',
    period: 'aylık',
    icon: Zap,
    popular: true,
    features: [
      '12 Post tasarım',
      '20 Story tasarım',
      '4 Reels kapak',
      '1 kampanya afişi + 2 boyut (post/story)'
    ],
    color: '#d4af37'
  },
  {
    id: 4,
    name: 'Pro',
    subtitle: 'Kampanya + Reklam',
    price: '16.900',
    period: 'aylık',
    icon: Crown,
    popular: false,
    features: [
      '16 Post tasarım',
      '30 Story tasarım',
      '6 Reels kapak',
      '2 kampanya seti (4 boyut varyasyonu)'
    ],
    color: '#d4af37'
  },
  {
    id: 5,
    name: 'Kurumsal Kimlik',
    subtitle: 'Marka Seti',
    price: '14.900',
    period: 'tek sefer',
    icon: Briefcase,
    popular: false,
    features: [
      'Logo tasarımı / düzenleme',
      'Renk–font sistemi + mini kılavuz',
      'Kartvizit + antet (PDF)',
      'Şablon seti: 6 post + 6 story'
    ],
    color: '#d4af37'
  },
  {
    id: 6,
    name: 'Sürekli Destek',
    subtitle: 'Tasarım Bankası',
    price: '21.900',
    period: 'aylık',
    icon: Infinity,
    popular: false,
    features: [
      'Aylık 25 tasarım hakkı (post/story/afiş/banner)',
      'Reklam kreatifleri (boyut setleri)',
      'Öncelikli teslim + 3 revize turu',
      'Dosya arşivi & şablon düzeni'
    ],
    color: '#d4af37'
  }
];

const PricingSection = () => {
  const [hoveredId, setHoveredId] = useState(null);

  const scrollToForm = () => {
    const formElement = document.getElementById('teklif-formu');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <section id="paketler" className="py-24 bg-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div 
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full"
          >
            <Crown className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-mono uppercase tracking-wider">Paketler</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Size Özel <span className="text-gold">Çözümler</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            İhtiyacınıza uygun profesyonel tasarım paketleri ile markanızı güçlendirin.
          </motion.p>
        </motion.div>

        {/* Pricing Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
        >
          {packages.map((pkg) => {
            const IconComponent = pkg.icon;
            const isHovered = hoveredId === pkg.id;
            
            return (
              <motion.div
                key={pkg.id}
                variants={cardVariants}
                onHoverStart={() => setHoveredId(pkg.id)}
                onHoverEnd={() => setHoveredId(null)}
                whileHover={{ y: -12 }}
                className={`relative bg-dark-grey/80 backdrop-blur-sm border rounded-2xl p-8 overflow-hidden ${
                  pkg.popular 
                    ? 'border-gold shadow-2xl shadow-gold/20 lg:scale-105' 
                    : 'border-white/10 hover:border-gold/30'
                }`}
              >
                {/* Popular Badge */}
                {pkg.popular && (
                  <motion.div
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="absolute top-4 right-4 px-3 py-1 bg-gold text-black text-xs font-bold rounded-full"
                  >
                    ÖNERİLEN
                  </motion.div>
                )}

                {/* Animated Background Glow */}
                <motion.div
                  animate={{
                    opacity: isHovered ? 0.2 : 0,
                    scale: isHovered ? 1.5 : 1,
                  }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 bg-gradient-to-br from-gold/20 via-transparent to-transparent"
                />

                {/* Icon */}
                <motion.div 
                  animate={{
                    rotate: isHovered ? 360 : 0,
                    scale: isHovered ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.6 }}
                  className="relative mb-6"
                >
                  <div className={`inline-flex p-4 rounded-2xl ${
                    pkg.popular ? 'bg-gold/20' : 'bg-gold/10'
                  }`}>
                    <IconComponent className="w-8 h-8 text-gold" />
                  </div>
                </motion.div>

                {/* Package Info */}
                <div className="relative mb-6">
                  <motion.h3 
                    animate={{ color: isHovered ? '#d4af37' : '#ffffff' }}
                    className="text-2xl font-bold mb-1"
                  >
                    {pkg.name}
                  </motion.h3>
                  <p className="text-gray-400 text-sm mb-4">{pkg.subtitle}</p>
                  
                  <div className="flex items-end gap-2 mb-2">
                    <motion.span 
                      animate={{ scale: isHovered ? 1.05 : 1 }}
                      className="text-4xl font-bold text-gold"
                    >
                      ₺{pkg.price}
                    </motion.span>
                    <span className="text-gray-400 text-sm mb-2">/ {pkg.period}</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 relative">
                  {pkg.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <motion.div
                        animate={{
                          scale: isHovered ? [1, 1.2, 1] : 1,
                        }}
                        transition={{ duration: 0.3, delay: idx * 0.05 }}
                      >
                        <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      </motion.div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA Button */}
                <motion.button
                  onClick={scrollToForm}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`relative w-full py-3 rounded-full font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-gold text-black hover:bg-gold-light shadow-lg hover:shadow-gold/50'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-gold hover:text-black hover:border-gold'
                  }`}
                >
                  <motion.span
                    animate={{ x: isHovered ? 5 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Teklif Al
                  </motion.span>
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <p className="text-gray-500 text-sm">
            <strong>Not:</strong> Reklam bütçesi pakete dahil değildir. Paketler; kurulum, yönetim ve optimizasyonu kapsar.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;